/**
 * S14 · Form Fill — single-field editor. Each `FieldKind` gets its own
 * mini-input: text, longText, number, select, multiselect, severity, boolean,
 * date, and the two that answer themselves with captures — photos and voice.
 *
 * Saving calls `useUpsertFieldValueMutation` which writes through SQLite and
 * enqueues `fieldValue.upsert`. Next/prev buttons move through the template's
 * field order.
 *
 * `signature` is here too, and getting there took correcting a wrong reading.
 * It looked like it needed a per-field media route, because the only thing
 * that sent a signature was `signature.upload` → `POST /signatures/{id}`,
 * which belongs to sign-off and takes a session. But a signature field does
 * not need that path: it is a PNG tied to a field, which is exactly what
 * `capture.upsert` + `media.upload` + `linked_field_id` already carry. The
 * missing piece was a `CaptureKind`, not a route.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { FieldValue, FormFieldDef } from '@kapture/types';
import { Btn, Chip, GestureBar, I, Ico, ScreenFrame, StatusBar, T } from '@/components/primitives';
import {
  useCaptureSessionStore,
  useCapturesBySession,
  useCreateSignatureMutation,
  useFieldValue,
  useSession,
  useUpsertFieldValueMutation,
} from '@kapture/client-core';
import { getFieldDef, getSectionDef, getTemplate } from '@kapture/domain';
import { LayoutOrLeaf } from '@/components/LayoutOrLeaf';
import { SignaturePad, type SignaturePadHandle } from '@/components/SignaturePad';

export const Route = createFileRoute('/sessions/$sessionId/form/$fieldId')({
  component: FormFillPage,
  staticData: { mobile: true },
});

function FormFillPage() {
  return (
    <LayoutOrLeaf routeId="/sessions/$sessionId/form/$fieldId">
      <FormFillContent />
    </LayoutOrLeaf>
  );
}

function FormFillContent() {
  const { sessionId, fieldId } = Route.useParams();
  const navigate = useNavigate();
  const { data: session } = useSession(sessionId);
  const { data: value } = useFieldValue(sessionId, fieldId);
  const upsert = useUpsertFieldValueMutation();

  const template = getTemplate(session?.templateId);
  const field = template ? getFieldDef(template, fieldId) : null;
  const section = field && template ? getSectionDef(template, field.sectionId) : null;

  // Prev/next navigation through the template's field list.
  const fieldIndex = useMemo(() => {
    if (!template || !field) return -1;
    return template.fields.findIndex((f) => f.id === field.id);
  }, [template, field]);
  const nextField = template && fieldIndex >= 0 ? template.fields[fieldIndex + 1] : null;
  const prevField = template && fieldIndex > 0 ? template.fields[fieldIndex - 1] : null;

  const [draft, setDraft] = useState<unknown>(value?.value ?? null);
  const [dirty, setDirty] = useState(false);
  /**
   * How the current draft got here.
   *
   * Saved with the value rather than assumed, because `source` is a
   * provenance claim a reviewer reads months later. Editing after a scan
   * returns it to `manual` — the moment a person changes the characters, a
   * decoder is no longer what produced them.
   */
  const [draftSource, setDraftSource] = useState<FieldValue['source']>('manual');

  useEffect(() => {
    if (!dirty) {
      setDraft(value?.value ?? null);
      setDraftSource(value?.source ?? 'manual');
    }
  }, [value, dirty]);

  if (!session || !template || !field) {
    return (
      <ScreenFrame tone="light">
        <StatusBar />
        <div
          style={{
            flex: 1,
            display: 'grid',
            placeItems: 'center',
            color: T.textDim,
            fontFamily: T.font,
            padding: 24,
          }}
        >
          {session && !template
            ? 'No template on this session.'
            : template && !field
              ? 'Unknown field.'
              : 'Loading…'}
        </div>
      </ScreenFrame>
    );
  }

  async function saveAnd(direction: 'stay' | 'next' | 'overview') {
    await upsert.mutateAsync({
      sessionId,
      fieldId,
      value: draft,
      source: draftSource,
    });
    setDirty(false);
    if (direction === 'next' && nextField) {
      navigate({
        to: '/sessions/$sessionId/form/$fieldId',
        params: { sessionId, fieldId: nextField.id },
      });
    } else if (direction === 'overview') {
      navigate({ to: '/sessions/$sessionId/form', params: { sessionId } });
    }
  }

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: `1px solid ${T.stroke}`,
        }}
      >
        <Link
          to="/sessions/$sessionId/form"
          params={{ sessionId }}
          aria-label="Back to form"
          style={{ color: T.text, display: 'flex', textDecoration: 'none' }}
        >
          <Ico d={I.back} size={20} />
        </Link>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 10,
              color: T.textMute,
              fontFamily: T.mono,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {section?.title ?? 'Field'} · {fieldIndex + 1} of {template.fields.length}
          </div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              fontFamily: T.font,
              letterSpacing: '-0.01em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {field.label}
          </div>
        </div>
        {value ? (
          <Chip tone="good" size="sm">
            Saved
          </Chip>
        ) : field.required ? (
          <Chip tone="warn" size="sm">
            Required
          </Chip>
        ) : (
          <Chip tone="neutral" size="sm">
            Optional
          </Chip>
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <FieldEditor
          sessionId={sessionId}
          field={field}
          value={draft}
          onChange={(v, source) => {
            setDraft(v);
            setDraftSource(source ?? 'manual');
            setDirty(true);
          }}
        />

        <button
          type="button"
          onClick={() =>
            navigate({
              to: '/sessions/$sessionId/form/$fieldId/suggestions',
              params: { sessionId, fieldId },
            })
          }
          style={{
            marginTop: 14,
            width: '100%',
            padding: '10px 12px',
            borderRadius: 10,
            border: `1px solid ${T.brand}`,
            background: T.brandSoft,
            color: T.brandInk,
            fontFamily: T.font,
            fontWeight: 600,
            fontSize: 13,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            cursor: 'pointer',
          }}
        >
          <Ico d={I.sparkle} size={14} fill={T.brand} stroke={T.brand} />
          Show AI suggestions
        </button>

        {value?.source && value.source !== 'manual' && (
          <div
            style={{
              marginTop: 14,
              padding: 10,
              borderRadius: 10,
              background: T.brandSoft,
              border: `1px solid ${T.brandSoft}`,
              color: T.brandInk,
              fontSize: 12,
              fontFamily: T.font,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Ico d={I.sparkle} size={12} fill={T.brand} stroke={T.brand} />
            <div style={{ flex: 1 }}>
              Sourced from <b>{value.source}</b>
              {value.confidence != null
                ? ` · ${Math.round(value.confidence * 100)}% confidence`
                : ''}
            </div>
          </div>
        )}

        {upsert.isError && (
          <div
            style={{
              marginTop: 14,
              padding: 10,
              borderRadius: 10,
              background: T.dangerSoft,
              border: `1px solid ${T.dangerSoft}`,
              color: T.danger,
              fontSize: 12,
              fontFamily: T.font,
            }}
          >
            Couldn't save. The capture is still local — try again.
          </div>
        )}
      </div>

      <div
        style={{
          padding: '10px 16px 12px',
          borderTop: `1px solid ${T.stroke}`,
          display: 'flex',
          gap: 8,
        }}
      >
        <Btn
          tone="light"
          size="md"
          icon={<Ico d={I.back} size={14} />}
          disabled={!prevField}
          onClick={() => {
            if (!prevField) return;
            navigate({
              to: '/sessions/$sessionId/form/$fieldId',
              params: { sessionId, fieldId: prevField.id },
            });
          }}
          style={{ opacity: prevField ? 1 : 0.4 }}
        >
          Prev
        </Btn>
        <Btn
          tone={nextField ? 'primary' : 'good'}
          size="md"
          full
          iconRight={<Ico d={nextField ? I.arrow : I.check2} size={16} sw={2.4} />}
          onClick={() => void saveAnd(nextField ? 'next' : 'overview')}
          disabled={upsert.isPending}
          style={{ opacity: upsert.isPending ? 0.6 : 1 }}
        >
          {upsert.isPending ? 'Saving…' : nextField ? 'Save & next' : 'Save & finish'}
        </Btn>
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}

function FieldEditor({
  sessionId,
  field,
  value,
  onChange,
}: {
  sessionId: string;
  field: FormFieldDef;
  value: unknown;
  onChange: (v: unknown, source?: FieldValue['source']) => void;
}) {
  switch (field.kind) {
    case 'text':
    case 'longText': {
      const placeholder =
        typeof field.config.placeholder === 'string' ? field.config.placeholder : '';
      // A scannable field is still a text field. The template author turns the
      // scanner on for the fields that carry a code — an asset id, a crossing
      // ref, a room number — without the product having to decide in advance
      // which of those counts as an "asset", and without a field kind whose
      // name commits it to one domain.
      if (field.kind === 'text' && field.config.scan === true) {
        return (
          <ScanTextInput
            sessionId={sessionId}
            fieldId={field.id}
            placeholder={placeholder}
            pattern={typeof field.config.pattern === 'string' ? field.config.pattern : undefined}
            value={typeof value === 'string' ? value : ''}
            onChange={onChange}
          />
        );
      }
      return (
        <TextInput
          multiline={field.kind === 'longText'}
          placeholder={placeholder}
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
        />
      );
    }
    case 'number':
      return (
        <NumberInput
          value={typeof value === 'number' ? value : null}
          onChange={onChange}
          unit={typeof field.config.unit === 'string' ? field.config.unit : undefined}
          min={typeof field.config.min === 'number' ? field.config.min : undefined}
          step={typeof field.config.step === 'number' ? field.config.step : undefined}
        />
      );
    case 'select':
      return (
        <SelectInput
          options={Array.isArray(field.config.options) ? (field.config.options as string[]) : []}
          value={typeof value === 'string' ? value : null}
          onChange={onChange}
        />
      );
    case 'multiselect':
      return (
        <MultiSelectInput
          options={Array.isArray(field.config.options) ? (field.config.options as string[]) : []}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={onChange}
        />
      );
    case 'severity':
      return (
        <SeverityInput
          options={
            Array.isArray(field.config.options)
              ? (field.config.options as Array<{ value: string; label: string }>)
              : []
          }
          value={typeof value === 'string' ? value : null}
          onChange={onChange}
        />
      );
    case 'signature':
      return <SignatureFieldInput sessionId={sessionId} fieldId={field.id} />;
    case 'photos':
    case 'voice':
      return (
        <MediaFieldInput
          sessionId={sessionId}
          fieldId={field.id}
          kind={field.kind}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={onChange}
        />
      );
    case 'boolean':
      return <BooleanInput value={value === true} onChange={onChange} />;
    case 'date':
      return <DateInput value={typeof value === 'string' ? value : ''} onChange={onChange} />;
    default:
      return (
        <div
          style={{
            padding: 16,
            borderRadius: 12,
            border: `1px dashed ${T.stroke2}`,
            color: T.textDim,
            fontSize: 13,
            fontFamily: T.font,
            lineHeight: 1.5,
          }}
        >
          <b style={{ fontWeight: 700, color: T.text }}>{field.kind}</b> fields have no editor yet.
          Any value already saved is kept and sent as-is.
        </div>
      );
  }
}

/* ---------------------------- Field sub-editors --------------------------- */

function TextInput({
  multiline,
  placeholder,
  value,
  onChange,
}: {
  multiline?: boolean;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const common = {
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(e.target.value),
    placeholder,
    style: {
      width: '100%',
      padding: '12px 14px',
      fontSize: 15,
      fontFamily: T.font,
      borderRadius: 10,
      border: `1px solid ${T.stroke}`,
      outline: 'none',
      background: T.surface,
      color: T.text,
      resize: multiline ? ('vertical' as const) : ('none' as const),
    },
  };
  return multiline ? (
    <textarea {...common} rows={6} style={{ ...common.style, minHeight: 140 }} />
  ) : (
    <input {...common} type="text" />
  );
}

function NumberInput({
  value,
  onChange,
  unit,
  min,
  step,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  unit?: string;
  min?: number;
  step?: number;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 14px',
        border: `1px solid ${T.stroke}`,
        borderRadius: 10,
        background: T.surface,
      }}
    >
      <input
        type="number"
        inputMode="decimal"
        min={min}
        step={step ?? 'any'}
        value={value == null ? '' : String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') onChange(null);
          else {
            const parsed = Number(raw);
            onChange(Number.isFinite(parsed) ? parsed : null);
          }
        }}
        style={{
          flex: 1,
          border: 'none',
          outline: 'none',
          fontFamily: T.mono,
          fontSize: 20,
          fontWeight: 600,
          color: T.text,
          background: 'transparent',
        }}
      />
      {unit && (
        <span
          style={{
            fontSize: 12,
            color: T.textMute,
            fontFamily: T.mono,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {unit}
        </span>
      )}
    </div>
  );
}

function SelectInput({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string | null;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            style={{
              padding: '12px 14px',
              borderRadius: 10,
              border: `1px solid ${active ? T.brand : T.stroke}`,
              background: active ? T.brandSoft : T.surface,
              color: active ? T.brandInk : T.text,
              fontFamily: T.font,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              textAlign: 'left',
              boxShadow: active ? '0 0 0 3px rgba(255,90,31,0.08)' : 'none',
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A signature drawn against a form field.
 *
 * Saves as an ordinary capture, `kind: 'signature'`, carrying this field's id
 * — so it uploads on the paths that already exist and a reviewer can follow
 * it back to the field it answers. The "Save" button below it saves the
 * *field value*; this one saves the signature, because a PNG and a field
 * value are two different writes and pretending otherwise would let the
 * inspector lose one by pressing the other.
 */
function SignatureFieldInput({ sessionId, fieldId }: { sessionId: string; fieldId: string }) {
  const padRef = useRef<SignaturePadHandle>(null);
  const [drawn, setDrawn] = useState(false);
  const { data: captures } = useCapturesBySession(sessionId);
  const create = useCreateSignatureMutation();

  const existing = (captures ?? []).filter(
    (c) => c.kind === 'signature' && c.linkedFieldId === fieldId,
  );

  async function save() {
    const blob = await padRef.current?.toBlob();
    if (!blob) return;
    await create.mutateAsync({
      sessionId,
      linkedFieldId: fieldId,
      media: blob,
      byteSize: blob.size,
    });
    padRef.current?.clear();
    setDrawn(false);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {existing.length > 0 && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: 10,
            background: T.goodSoft,
            color: T.good,
            fontSize: 13,
            fontFamily: T.font,
          }}
        >
          Signed · {existing.length} on file. Drawing another adds one rather than replacing — a
          signature is a record, not a setting.
        </div>
      )}

      <SignaturePad ref={padRef} onDrawnChange={setDrawn} />

      <div style={{ display: 'flex', gap: 8 }}>
        <Btn
          tone="light"
          size="md"
          onClick={() => {
            padRef.current?.clear();
            setDrawn(false);
          }}
          disabled={!drawn}
        >
          Clear
        </Btn>
        <Btn
          tone="primary"
          size="md"
          full
          onClick={() => void save()}
          disabled={!drawn || create.isPending}
        >
          {create.isPending ? 'Saving…' : 'Save signature'}
        </Btn>
      </div>
    </div>
  );
}

/**
 * A field whose answer is captures rather than typing.
 *
 * `photos` and `voice` fields are answered by recording something, so this
 * does not edit a value directly — it launches the ordinary capture route
 * with `linkedFieldId` set, and reads back the captures that came home
 * carrying this field's id.
 *
 * `linkedFieldId` is the piece that made this cheap: the column, the Zod
 * field and the insert path have existed since the schema was written, with
 * every caller passing null. Setting it is most of the feature.
 *
 * The stored value mirrors the linked capture ids. The captures are the truth
 * — the value exists so the form overview can tell an answered field from an
 * empty one without loading every capture in the session.
 */
function MediaFieldInput({
  sessionId,
  fieldId,
  kind,
  value,
  onChange,
}: {
  sessionId: string;
  fieldId: string;
  kind: 'photos' | 'voice';
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const navigate = useNavigate();
  const { data: captures } = useCapturesBySession(sessionId);
  const setLinkedField = useCaptureSessionStore((s) => s.setLinkedField);
  const setActiveSession = useCaptureSessionStore((s) => s.setActiveSession);

  const wanted = kind === 'photos' ? 'photo' : 'voice';
  const linked = useMemo(
    () => (captures ?? []).filter((c) => c.linkedFieldId === fieldId && c.kind === wanted),
    [captures, fieldId, wanted],
  );

  // Reconcile the stored value with what actually came back. Compared as a
  // joined key so this settles after one pass instead of looping on a fresh
  // array identity every render.
  const ids = linked.map((c) => c.id);
  const idKey = ids.join(',');
  const valueKey = value.join(',');
  useEffect(() => {
    if (idKey !== valueKey) onChange(idKey ? idKey.split(',') : []);
    // `onChange` is a fresh closure each render; keying on the two strings is
    // what makes this converge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey, valueKey]);

  function capture() {
    setActiveSession(sessionId);
    setLinkedField(fieldId);
    navigate({
      to:
        kind === 'photos'
          ? '/sessions/$sessionId/capture/photo'
          : '/sessions/$sessionId/capture/voice',
      params: { sessionId },
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {linked.length === 0 ? (
        <div
          style={{
            padding: 16,
            borderRadius: 12,
            border: `1px dashed ${T.stroke2}`,
            color: T.textDim,
            fontSize: 13,
            fontFamily: T.font,
            textAlign: 'center',
          }}
        >
          {kind === 'photos' ? 'No photos yet.' : 'No recordings yet.'}
        </div>
      ) : (
        linked.map((c, i) => (
          <Link
            key={c.id}
            to="/sessions/$sessionId/captures/$captureId"
            params={{ sessionId, captureId: c.id }}
            style={{
              padding: '12px 14px',
              borderRadius: 10,
              border: `1px solid ${T.stroke}`,
              background: T.surface,
              color: T.text,
              fontFamily: T.font,
              fontSize: 14,
              fontWeight: 600,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <Ico d={kind === 'photos' ? I.image : I.mic} size={16} stroke={T.brand} />
            <span style={{ flex: 1 }}>
              {kind === 'photos' ? 'Photo' : 'Recording'} {i + 1}
            </span>
            <Chip tone="neutral" size="sm">
              {new Date(c.createdAt).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Chip>
          </Link>
        ))
      )}

      <Btn tone="dark" size="md" full onClick={capture}>
        {kind === 'photos' ? 'Take a photo' : 'Record'}
      </Btn>
    </div>
  );
}

/**
 * Select, but many. Kept as its own component rather than a mode on
 * `SelectInput` because the semantics differ where it matters: tapping an
 * active option here removes it, and the stored value is an array even when
 * one thing is chosen. A reader downstream can tell "one option" from "a list
 * that happens to have one item" — which a shared component returning
 * `string | string[]` would have blurred.
 */
function MultiSelectInput({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  function toggle(opt: string) {
    // Rebuilt from `options` rather than appending, so the saved order is the
    // template's order however the inspector tapped through it.
    const next = new Set(value);
    if (next.has(opt)) next.delete(opt);
    else next.add(opt);
    onChange(options.filter((o) => next.has(o)));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {options.map((opt) => {
        const active = value.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            role="checkbox"
            aria-checked={active}
            onClick={() => toggle(opt)}
            style={{
              padding: '12px 14px',
              borderRadius: 10,
              border: `1px solid ${active ? T.brand : T.stroke}`,
              background: active ? T.brandSoft : T.surface,
              color: active ? T.brandInk : T.text,
              fontFamily: T.font,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              boxShadow: active ? '0 0 0 3px rgba(255,90,31,0.08)' : 'none',
            }}
          >
            <span
              aria-hidden
              style={{
                width: 18,
                height: 18,
                borderRadius: 5,
                flexShrink: 0,
                border: `1.5px solid ${active ? T.brand : T.stroke2}`,
                background: active ? T.brand : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              {active && <Ico d={I.check} size={12} stroke="#fff" />}
            </span>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function SeverityInput({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: string; label: string }>;
  value: string | null;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            style={{
              padding: '14px 12px',
              borderRadius: 12,
              border: `1px solid ${active ? T.brand : T.stroke}`,
              background: active ? T.brand : T.surface,
              color: active ? '#fff' : T.text,
              fontFamily: T.font,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function BooleanInput({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {[true, false].map((v) => {
        const active = value === v;
        return (
          <button
            key={v ? 'yes' : 'no'}
            type="button"
            onClick={() => onChange(v)}
            style={{
              flex: 1,
              padding: '14px 12px',
              borderRadius: 12,
              border: `1px solid ${active ? T.brand : T.stroke}`,
              background: active ? T.brand : T.surface,
              color: active ? '#fff' : T.text,
              fontFamily: T.font,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {v ? 'Yes' : 'No'}
          </button>
        );
      })}
    </div>
  );
}

function DateInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%',
        padding: '12px 14px',
        fontSize: 15,
        fontFamily: T.font,
        borderRadius: 10,
        border: `1px solid ${T.stroke}`,
        outline: 'none',
        background: T.surface,
        color: T.text,
      }}
    />
  );
}

/**
 * A text field the scanner can fill.
 *
 * Not its own `FieldKind`. What varies between "Asset ID", "Crossing ID",
 * "Room number" and "Serial" is not the shape of the answer — every one of
 * them is a string — but whether there is a label to read it off. Making that
 * a kind would force every template author to decide whether their thing is
 * an asset, and half of them are inspecting an outlet, a crossing or a room.
 * A flag on `text` costs no schema on either side and leaves the vocabulary
 * where it belongs: with the customer who wrote the template.
 *
 * `pattern` is theirs too. National Highways declares what a bridge reference
 * looks like; nothing here learns what the string means.
 */
function ScanTextInput({
  sessionId,
  fieldId,
  placeholder,
  pattern,
  value,
  onChange,
}: {
  sessionId: string;
  fieldId: string;
  placeholder: string;
  pattern?: string;
  value: string;
  onChange: (v: string, source?: FieldValue['source']) => void;
}) {
  const navigate = useNavigate();
  const { data: captures } = useCapturesBySession(sessionId);
  const setLinkedField = useCaptureSessionStore((s) => s.setLinkedField);
  const setActiveSession = useCaptureSessionStore((s) => s.setActiveSession);

  // The scan that came home carrying this field's id, newest first. The
  // capture is the evidence; the string is the answer.
  const scan = useMemo(() => {
    const mine = (captures ?? []).filter(
      (c) => c.kind === 'barcode' && c.linkedFieldId === fieldId,
    );
    return mine.length ? mine[mine.length - 1] : null;
  }, [captures, fieldId]);

  // Adopt it once. A later edit is the inspector's, and must not be reverted
  // by a scan still sitting in the session.
  const adopted = useRef<string | null>(null);
  useEffect(() => {
    if (!scan || scan.kind !== 'barcode') return;
    if (adopted.current === scan.id) return;
    adopted.current = scan.id;
    if (scan.value !== value) onChange(scan.value, scan.format ? 'scan' : 'manual');
    // `value` is deliberately absent: this reacts to a new scan, not to typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scan]);

  const mismatch = Boolean(pattern) && value.length > 0 && !safeMatch(pattern!, value);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => onChange(e.target.value, 'manual')}
          style={{
            flex: 1,
            minWidth: 0,
            padding: '12px 14px',
            fontSize: 15,
            fontFamily: T.mono,
            borderRadius: 10,
            border: `1px solid ${mismatch ? T.warnText : T.stroke}`,
            outline: 'none',
            background: T.surface,
            color: T.text,
          }}
        />
        <Btn
          tone="dark"
          size="md"
          onClick={() => {
            setActiveSession(sessionId);
            setLinkedField(fieldId);
            navigate({ to: '/sessions/$sessionId/capture/barcode', params: { sessionId } });
          }}
        >
          Scan
        </Btn>
      </div>

      {mismatch && (
        /*
         * A warning, not a block. The pattern catches the shipping label
         * scanned off the crate next to the asset — but a code that does not
         * match can still be the real one, on a plate replaced out of sequence
         * or a site the template was not written for, and a form that refuses
         * to record what is actually on the wall strands the inspector with
         * nowhere to put the truth.
         */
        <div style={{ fontSize: 12, color: T.warnText, fontFamily: T.font, lineHeight: 1.4 }}>
          This doesn't match the format this field expects. Save it anyway if it's what's on the
          label.
        </div>
      )}

      {scan?.kind === 'barcode' && (
        <Link
          to="/sessions/$sessionId/captures/$captureId"
          params={{ sessionId, captureId: scan.id }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            fontFamily: T.mono,
            color: T.textMute,
            textDecoration: 'none',
          }}
        >
          <Ico d={I.tag} size={12} stroke={T.textMute} />
          {scan.format ? `Scanned ${scan.format}` : 'Typed on the scan screen'} · view evidence
        </Link>
      )}
    </div>
  );
}

/**
 * A template author's regex, run without letting a bad one take the screen down.
 *
 * The pattern comes from customer-authored template config, so it is untrusted
 * input in the only sense that matters here: `new RegExp` throws on a
 * malformed one, and this app has no error boundary. An unusable pattern
 * declines to complain rather than blocking the field.
 */
function safeMatch(pattern: string, value: string): boolean {
  try {
    return new RegExp(pattern).test(value);
  } catch {
    return true;
  }
}
